import { useState, useRef, useEffect, type CSSProperties } from "react";
import { useNavigate, useParams } from "react-router";
import { useAuth } from "../../../lib/hooks/use-auth";
import { useWorkspaceInfo } from "../../../lib/hooks/use-kuvio";
import { KuvioIcon } from "../../brand/KuvioIcon";
import { switchWorkspaceWithHint } from "./switch-workspace";


export function WorkspaceSwitcher({ surface = "console" }: { surface?: "console" | "app" } = {}) {
  const { user } = useAuth();
  const wsInfo = useWorkspaceInfo();
  const navigate = useNavigate();
  const { nodeId } = useParams<{ nodeId: string }>();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // №13.4.2 (D-NAV2) — the SAME switcher on both surfaces (workspace identity lives
  // in top chrome only; the rail stays pure). Nav targets are surface-aware so an
  // App user is never ejected to a console route.
  const homeRoute = surface === "app" ? "/app" : "/workforce";
  const postSwitchRoute = surface === "app" ? "/app" : "/";

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const currentName = wsInfo.data?.workspace?.name ?? "Workspace";
  const rootNodeId = wsInfo.data?.root_node_id as string | undefined;
  const workspaces = user?.workspaces ?? [];
  const isOnWorkspaceCockpit = !!(rootNodeId && nodeId === rootNodeId);
  // D-NAV2: a single-workspace user sees the name only — no dead menu.
  const multiWorkspace = workspaces.length > 1;

  async function handleSwitch(id: string) {
    setOpen(false);
    await switchWorkspaceWithHint(id);
    navigate(postSwitchRoute);
  }

  // №14.1 — on the App surface the switcher is the canonical `.wswitch` bordered
  // pill (name + chevron in one box, text-sm/medium); console keeps its own look.
  const isApp = surface === "app";

  const nameStyle: CSSProperties = isApp
    ? {
        display: "inline-flex", alignItems: "center", gap: 8, border: "none",
        background: "transparent", fontSize: 13, fontWeight: 600,
        color: "var(--color-text-primary)", cursor: "pointer", padding: 0,
        fontFamily: "var(--font-sans)",
      }
    : {
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        border: "none",
        background: isOnWorkspaceCockpit ? "var(--color-background-secondary)" : "transparent",
        fontSize: 14,
        fontWeight: 500,
        color: "var(--color-text-primary)",
        cursor: "pointer",
        padding: "4px 8px",
        borderRadius: "var(--border-radius-md) 0 0 var(--border-radius-md)",
        transition: "background 120ms ease",
        fontFamily: "var(--font-sans)",
      };

  const chevronStyle: CSSProperties = isApp
    ? {
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        border: "none", background: "transparent", cursor: "pointer",
        padding: "0 0 0 2px", color: "var(--color-text-tertiary)", fontFamily: "var(--font-sans)",
      }
    : {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        border: "none",
        background: "transparent",
        cursor: "pointer",
        padding: "4px 6px",
        borderRadius: "0 var(--border-radius-md) var(--border-radius-md) 0",
        color: "var(--color-text-tertiary)",
        transition: "background 120ms ease",
        borderLeft: "0.5px solid var(--color-border-tertiary)",
        fontFamily: "var(--font-sans)",
      };

  const dropdownStyle: CSSProperties = {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    minWidth: 240,
    background: "var(--color-background-primary)",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-md)",
    padding: 4,
    zIndex: 50,
    fontFamily: "var(--font-sans)",
  };

  const itemStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    padding: "8px 10px",
    border: "none",
    background: "transparent",
    fontSize: 13,
    textAlign: "left",
    borderRadius: "var(--border-radius-sm)",
    color: "var(--color-text-primary)",
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
  };

  const sectionLabel: CSSProperties = {
    fontSize: 11,
    fontWeight: 500,
    color: "var(--color-text-tertiary)",
    padding: "8px 10px 4px",
  };

  const workspaceHeader: CSSProperties = {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--color-text-primary)",
    padding: "6px 10px 8px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  const separator: CSSProperties = {
    height: 0,
    borderTop: "0.5px solid var(--color-border-tertiary)",
    margin: "4px 0",
  };

  return (
    <div
      ref={ref}
      className={isApp ? "wswitch" : undefined}
      style={{ position: "relative", display: "inline-flex", alignItems: "center" }}
    >
      <button
        style={multiWorkspace ? nameStyle : { ...nameStyle, borderRadius: isApp ? undefined : "var(--border-radius-md)" }}
        onClick={() => navigate(homeRoute)}
        onMouseEnter={isApp ? undefined : (e) => { (e.currentTarget as HTMLElement).style.background = "var(--color-background-secondary)"; }}
        onMouseLeave={isApp ? undefined : (e) => { (e.currentTarget as HTMLElement).style.background = isOnWorkspaceCockpit ? "var(--color-background-secondary)" : "transparent"; }}
        aria-label={`Go to ${surface === "app" ? "app home" : "workspace home"}, ${currentName}`}
      >
        {/* №14.1 (D-DS4) — ONE identity slot. The App top chrome already carries the
            product mark (the Ghost-Grid K); the workspace switcher is the workspace
            NAME + chevron only, no second mark. (Console keeps the Kuvio icon.) */}
        {surface === "console" && <KuvioIcon size={20} />}
        <span>{currentName}</span>
      </button>
      {multiWorkspace && (
        <button
          style={chevronStyle}
          onClick={() => setOpen((v) => !v)}
          onMouseEnter={isApp ? undefined : (e) => { (e.currentTarget as HTMLElement).style.background = "var(--color-background-secondary)"; }}
          onMouseLeave={isApp ? undefined : (e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Workspace menu"
        >
          <i
            className="ti ti-chevron-down"
            style={{
              fontSize: 14,
              transition: "transform 120ms ease",
              transform: open ? "rotate(180deg)" : "none",
            }}
            aria-hidden="true"
          />
        </button>
      )}
      {multiWorkspace && open && (
        <div style={dropdownStyle}>
          <div style={workspaceHeader}>{currentName}</div>

          {/* Home/settings are console (maker) routes — omitted in the App surface
              so a product user is never ejected into the console. */}
          {surface === "console" && (
            <>
              <button
                style={itemStyle}
                onClick={() => { setOpen(false); navigate("/home"); }}
              >
                <i className="ti ti-home" style={{ fontSize: 14 }} aria-hidden="true" />
                Workspace home
              </button>
              <button
                style={itemStyle}
                onClick={() => { setOpen(false); navigate("/settings/workspace"); }}
              >
                <i className="ti ti-settings" style={{ fontSize: 14 }} aria-hidden="true" />
                Workspace settings
              </button>
              <div style={separator} />
            </>
          )}

          <div style={sectionLabel}>Switch workspace</div>
          {workspaces.map((ws) => {
            const isCurrent = ws.id === (wsInfo.data?.workspace as { id?: string } | undefined)?.id;
            return (
              <button
                key={ws.id}
                style={itemStyle}
                onClick={() => { if (!isCurrent) handleSwitch(ws.id); }}
              >
                <span style={{ flex: 1 }}>{ws.name}</span>
                {isCurrent && (
                  <i className="ti ti-check" style={{ fontSize: 14, color: "var(--color-text-tertiary)" }} aria-hidden="true" />
                )}
              </button>
            );
          })}
          {workspaces.length === 0 && (
            <div style={{ ...itemStyle, color: "var(--color-text-tertiary)", cursor: "default" }}>
              No workspaces
            </div>
          )}

          <div style={separator} />

          <button
            style={{ ...itemStyle, color: "var(--color-text-tertiary)", cursor: "default", opacity: 0.6 }}
            aria-disabled="true"
            title="Coming soon"
          >
            <i className="ti ti-plus" style={{ fontSize: 14 }} aria-hidden="true" />
            + New workspace
          </button>
        </div>
      )}
    </div>
  );
}
